import { findSensitive, isClean, redactText, type SensitiveKind } from "@paredown/shared";

/**
 * Pure part of the in-browser scrubber, kept free of pdf.js so it can be unit
 * tested. A page is a list of positioned text items; the scrubber decides
 * which items to paint over and produces the redacted text layer.
 */

export interface TextItem {
  str: string;
  /** Bounding box in PDF user space (origin bottom-left). */
  x: number;
  y: number;
  width: number;
  height: number;
  /** True when pdf.js reports a line break after this item. */
  eol?: boolean;
}

export interface Box {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface PageRedaction {
  /** Redacted text of this page. */
  text: string;
  /** Boxes to paint over, in PDF user space. */
  boxes: Box[];
}

export interface RedactionPlan {
  pages: PageRedaction[];
  /** Full redacted text layer, pages separated by a marker. */
  text: string;
  tokens: Record<string, SensitiveKind>;
  /** Number of sensitive values found across all pages. */
  found: number;
}

/** Join items into page text while remembering where each item landed. */
export function layoutPage(items: TextItem[]): { text: string; spans: Array<{ start: number; end: number; item: TextItem }> } {
  let text = "";
  const spans: Array<{ start: number; end: number; item: TextItem }> = [];
  let prev: TextItem | undefined;
  for (const item of items) {
    if (prev) {
      const sameLine = Math.abs(prev.y - item.y) < Math.max(2, prev.height * 0.5);
      text += prev.eol || !sameLine ? "\n" : " ";
    }
    const start = text.length;
    text += item.str;
    spans.push({ start, end: text.length, item });
    prev = item;
  }
  return { text, spans };
}

const PAD = 2;
/** Slack on each side of a box, as a fraction of a digit's width, to absorb font differences. */
const SLACK_DIGITS = 0.6;

/**
 * Character advance widths of Helvetica/Arial in 1/1000 em, which is what
 * nearly every statement is set in. pdf.js only reports an item's total
 * width, so these give each character its share of it; the result is scaled
 * to the item's real width, so a wider or narrower font still lands close.
 */
const WIDTHS: Record<string, number> = {
  " ": 278, ".": 278, ",": 278, ":": 278, ";": 278, "/": 278, "'": 191, "-": 333, "(": 333, ")": 333, "*": 389, "#": 556, "&": 667, "@": 1015,
  "0": 556, "1": 556, "2": 556, "3": 556, "4": 556, "5": 556, "6": 556, "7": 556, "8": 556, "9": 556,
  A: 667, B: 667, C: 722, D: 722, E: 667, F: 611, G: 778, H: 722, I: 278, J: 500, K: 667, L: 556, M: 833, N: 722, O: 778, P: 667, Q: 778, R: 722, S: 667, T: 611, U: 722, V: 667, W: 944, X: 667, Y: 667, Z: 611,
  a: 556, b: 556, c: 500, d: 556, e: 556, f: 278, g: 556, h: 556, i: 222, j: 222, k: 500, l: 222, m: 833, n: 556, o: 556, p: 556, q: 556, r: 333, s: 500, t: 278, u: 556, v: 500, w: 722, x: 500, y: 500, z: 500,
};
const widthOf = (ch: string) => WIDTHS[ch] ?? 600;

/** Estimated left edge of each character in an item, plus the right edge of the last, in PDF units. */
export function charEdges(item: TextItem): number[] {
  return measure(item).edges;
}

function measure(item: TextItem): { edges: number[]; digitWidth: number } {
  const units = [...item.str].map(widthOf);
  const total = units.reduce((a, b) => a + b, 0) || 1;
  const scale = item.width / total;
  const edges = [item.x];
  for (const u of units) edges.push(edges[edges.length - 1]! + u * scale);
  return { edges, digitWidth: 556 * scale };
}

/**
 * The box for the characters [localStart, localEnd) of one text item. Only the
 * matched characters are painted, so the merchant name around a reference
 * number survives: the slack on each side may only spill into whitespace next
 * to the match (or past the item's edge), never onto a neighbouring letter.
 * With `exactEnd` the right edge sits exactly on the character boundary, for
 * when the digits after it are meant to stay readable.
 */
export function boxFor(item: TextItem, localStart: number, localEnd: number, exactEnd = false): Box {
  const chars = [...item.str];
  const len = chars.length;
  const y = item.y - PAD;
  const height = Math.max(item.height, 6) + PAD * 2;
  if (len === 0 || item.width <= 0 || localEnd <= localStart) return { x: item.x - PAD, y, width: item.width + PAD * 2, height };
  const { edges, digitWidth } = measure(item);
  const slack = digitWidth * SLACK_DIGITS + PAD;
  const first = Math.max(0, localStart);
  const last = Math.min(len, localEnd);
  // Room to spill: PAD past the item's edges, the gap's width when a space sits next to the match, none otherwise.
  const room = (i: number) => (i < 0 || i >= len ? PAD : /\s/.test(chars[i]!) ? edges[i + 1]! - edges[i]! : 0);
  const x0 = edges[first]! - Math.min(slack, room(first - 1));
  const x1 = exactEnd ? edges[last]! : edges[last]! + Math.min(slack, room(last));
  return { x: x0, y, width: x1 - x0, height };
}

export interface PlanOptions {
  /**
   * How many trailing digits of a bare reference number stay visible in the
   * image, so the user can match it to an order or receipt (0 = none). Card,
   * account and routing numbers are always painted in full.
   */
  visibleTail?: number;
}

/** The end of the part of a match to paint, leaving its last `tail` digits visible. */
export function paintEnd(matchText: string, tail: number): number {
  if (tail <= 0) return matchText.length;
  let digits = 0;
  for (let i = matchText.length - 1; i >= 0; i--) {
    if (/\d/.test(matchText[i]!)) digits++;
    if (digits === tail) return i;
  }
  return 0;
}

/**
 * `tokenMap` (identity -> token) carries tokens across uploads so the same
 * card keeps the same letter; it is mutated with any new tokens.
 */
export function planRedaction(
  pages: TextItem[][],
  tokenMap: Map<string, string> = new Map(),
  options: PlanOptions = {},
): RedactionPlan {
  const tail = options.visibleTail ?? 0;
  const tokens: Record<string, SensitiveKind> = {};
  const out: PageRedaction[] = [];
  let found = 0;
  pages.forEach((items) => {
    const { text, spans } = layoutPage(items);
    const matches = findSensitive(text);
    found += matches.length;
    const boxes: Box[] = [];
    for (const m of matches) {
      const end = m.start + paintEnd(m.text, m.kind === "number" ? tail : 0);
      const trimmed = end < m.end;
      for (const s of spans) {
        if (s.start < end && s.end > m.start) {
          // Paint only the matched characters of this item (plus slack), so a
          // reference number on a merchant line does not hide the merchant.
          const localEnd = Math.min(end, s.end) - s.start;
          boxes.push(boxFor(s.item, Math.max(m.start, s.start) - s.start, localEnd, trimmed && end <= s.end));
        }
      }
    }
    const redacted = redactText(text, tokenMap);
    Object.assign(tokens, redacted.tokens);
    out.push({ text: redacted.text, boxes });
  });
  const text = out.map((p, i) => `--- page ${i + 1} ---\n${p.text}`).join("\n\n");
  if (!isClean(text)) throw new Error("redaction left a sensitive number behind; refusing to continue");
  return { pages: out, text, tokens, found };
}

export async function sha256Hex(text: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(text));
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, "0")).join("");
}
